import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { MatDialog } from '@angular/material/dialog';

import { clone } from '../../common/utils';
import { APP_BASE_URL, LOCAL_STORAGE_KEY } from '../../common/constants';

export interface AuthUser {
  adminFlag: boolean;
  permissionConventionFlag: boolean;
  permissionElectionFlag: boolean;
  id: number;
}

export interface JwtPayload {
  adminFlag: boolean;
  permissionConventionFlag: boolean;
  permissionElectionFlag: boolean;
  exp: number;
  iat: number;

  id: number;
}

export interface UserImpersonationState {
  firstName: string;
  lastName: string;
  expiresAt: number;
}

@Injectable()
export class AuthService {
  public authToken: string = null;
  public refresh: string = null;
  public expires: number = 0;
  public user: AuthUser = null;
  public impersonation: UserImpersonationState = null;

  protected readonly authTokenKey: string = LOCAL_STORAGE_KEY.AUTH.AUTH_TOKEN;
  protected readonly expiresKey: string = LOCAL_STORAGE_KEY.AUTH.EXPIRES_IN;
  protected readonly refreshKey: string = LOCAL_STORAGE_KEY.AUTH.REFRESH_TOKEN;
  protected readonly userKey: string = LOCAL_STORAGE_KEY.AUTH.USER;

  constructor(public readonly router: Router, private readonly dialog: MatDialog) {}

  loggedIn(): boolean {
    return !!(this.getAuthToken() && this.getUser());
  }

  setAuthToken(token: string): void {
    localStorage.setItem(this.authTokenKey, token);
    this.authToken = token;
  }

  getAuthToken(): string {
    if (localStorage.getItem(this.authTokenKey) && localStorage.getItem(this.authTokenKey) !== '') {
      const expires = Number(localStorage.getItem(this.expiresKey));
      const seconds = Math.floor(new Date().getTime() / 1000);

      if (seconds >= expires) {
        this.router.navigate(['/logout']);
        this.logout();
      } else {
        this.setAuthToken(localStorage.getItem(this.authTokenKey));
      }
    } else {
      return '';
    }

    return this.authToken;
  }

  setRefresh(refresh: string): void {
    localStorage.setItem(this.refreshKey, refresh);
    this.refresh = refresh;
  }

  getRefresh(): string {
    if (localStorage.getItem(this.refreshKey) && localStorage.getItem(this.refreshKey) !== '') {
      this.setRefresh(localStorage.getItem(this.refreshKey));
    }

    return this.refresh;
  }

  setExpires(expires: number): void {
    localStorage.setItem(this.expiresKey, String(expires));
    this.expires = expires;
  }

  getExpires(): number {
    if (localStorage.getItem(this.expiresKey) && localStorage.getItem(this.expiresKey) !== '') {
      this.setExpires(Number(localStorage.getItem(this.expiresKey)));
    }

    return this.expires;
  }

  setUser(user: AuthUser): void {
    localStorage.setItem(this.userKey, JSON.stringify(user));
    this.user = user;
  }

  getUser(): AuthUser {
    if (localStorage.getItem(this.userKey)) {
      this.user = JSON.parse(localStorage.getItem(this.userKey));
    }

    return clone(this.user);
  }

  parseTokenAndSetState(token: string, impersonation: Omit<UserImpersonationState, 'expiresAt'> = null): JwtPayload {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload: JwtPayload = JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join('')
      )
    );

    const { adminFlag, permissionConventionFlag, permissionElectionFlag, id } = payload;

    this.setAuthToken(token);
    this.setExpires(payload.exp);
    this.setUser({
      adminFlag: !!adminFlag,
      permissionConventionFlag: !!permissionConventionFlag,
      permissionElectionFlag: !!permissionElectionFlag,
      id,
    });

    return payload;
  }

  hasAnyModuleAccess(): boolean {
    const user = this.getUser();

    if (!user) {
      return false;
    }

    return !!(user.permissionConventionFlag || user.permissionElectionFlag || user.adminFlag);
  }

  canAccessSection(section: string): boolean {
    const user = this.getUser();

    if (!user) {
      return false;
    }

    switch (section) {
      case 'admin':
        return !!user.adminFlag;
      case 'conventions':
        return !!user.permissionConventionFlag;
      case 'elections':
        return !!user.permissionElectionFlag;
      default:
        return false;
    }
  }

  getDefaultAppRoute(): string[] | null {
    if (this.canAccessSection('conventions')) {
      return [APP_BASE_URL, 'conventions'];
    }

    if (this.canAccessSection('elections')) {
      return [APP_BASE_URL, 'elections'];
    }

    if (this.canAccessSection('admin')) {
      return [APP_BASE_URL, 'admin'];
    }

    return null;
  }

  logout() {
    this.setAuthToken(null);
    this.setRefresh(null);
    this.setExpires(null);
    this.setUser(null);

    localStorage.removeItem(this.authTokenKey);
    localStorage.removeItem(this.expiresKey);
    localStorage.removeItem(this.refreshKey);
    localStorage.removeItem(this.userKey);
  }
}
